/**
 * Cross-validation strip (FA pack — FA10).
 *
 * Compares the financial-model capacity against the site's
 * ProjectIntent power requirement and renders a one-line warning
 * strip with a "Sync to intent" affordance.
 *
 * Mounts above the FinancialAssumptionsForm so the user sees it
 * before editing. Reads `crossValidateFinancials()` for the result
 * + `syncCapacityToIntentPatch()` for the one-click sync target.
 *
 * Pure read on FA + ProjectIntent. Sync goes through the parent's
 * `onSync(patch)` callback (which calls
 * `updateSiteFinancialAssumptionsField` with a `manual` provenance
 * stamp).
 */
import React from 'react';
import { AlertTriangle, Info, CheckCircle2, ArrowRightCircle } from 'lucide-react';
import {
  crossValidateFinancials,
  syncCapacityToIntentPatch,
} from '../../../lib/financialCrossValidate';
import type { FinancialAssumptionsCore } from '../../../types/financialAssumptions';
import type { ProjectIntent } from '../../../types/siteRag';

export interface CrossValidationStripProps {
  /** Financial assumptions to validate. */
  fa: FinancialAssumptionsCore | null;
  /** Site's declared ProjectIntent (waterfall: report override → site → undefined). */
  intent: ProjectIntent | undefined;
  /** Called when the user clicks "Sync to intent". Patch is `{capacityMW: N}`. */
  onSync?: (patch: { capacityMW: number }) => void;
}

export function CrossValidationStrip({ fa, intent, onSync }: CrossValidationStripProps): React.JSX.Element | null {
  const result = crossValidateFinancials(fa, intent);
  // No-comparison case — render nothing.
  if (result.severity === 'ok' && result.financialMw == null && result.intentMw == null) {
    return null;
  }

  const patch = syncCapacityToIntentPatch(intent);
  const canSync = patch != null && onSync != null && result.severity !== 'ok';

  const tone = toneStyle(result.severity);

  return (
    <div className={`flex items-center gap-2 px-3 py-2 border rounded-lg ${tone.border} ${tone.bg}`}>
      <span className={`shrink-0 ${tone.text}`} aria-hidden>
        {result.severity === 'warning' ? <AlertTriangle className="w-3.5 h-3.5" />
          : result.severity === 'info' ? <Info className="w-3.5 h-3.5" />
          : <CheckCircle2 className="w-3.5 h-3.5" />}
      </span>
      <span className={`text-[11px] flex-1 ${tone.text}`}>{result.message}</span>
      {canSync && (
        <button
          type="button"
          onClick={() => onSync!(patch!)}
          className={`shrink-0 inline-flex items-center gap-1 type-meta px-2 py-1 border rounded-md ${tone.border} hover:bg-state-hover`}
        >
          <ArrowRightCircle className="w-3 h-3" /> Sync to intent
        </button>
      )}
    </div>
  );
}

interface ToneStyle {
  border: string;
  bg: string;
  text: string;
}

function toneStyle(severity: 'ok' | 'info' | 'warning'): ToneStyle {
  switch (severity) {
    case 'ok':      return { border: 'border-success/40', bg: 'bg-success/5',  text: 'text-success' };
    case 'info':    return { border: 'border-primary/40', bg: 'bg-primary/5',  text: 'text-primary' };
    case 'warning': return { border: 'border-warning/50', bg: 'bg-warning/10', text: 'text-warning' };
  }
}
